<template>
  <base-filter-select
      class="user-select-box"
      v-model="model"
      :options="userOptions"
      label="fullName"
      :reduce="user => user.id"
      :multiple="multi || selectProps.multiple"
      :clearable="clearable"
      :select-props="mergedSelectProps"
      :placeholder="placeholder"
  >
    <template #option="option">
      <div v-if="option._isGroup" class="desk-opt-header">{{ option.name }}</div>
      <div v-else class="d-flex align-content-center align-items-center">
        <b-avatar
            size="20"
            variant="light-primary"
            :src="require(`@/assets/images/avatars/${option.avatar || '1'}-small.png`)"
            class="mr-1"
        />
        <div>
          <p class="m-0 font-small-2 p-0">{{ option.fullName }}</p>
          <p class="m-0 font-small-1 text-muted p-0" v-if="showRoles">{{ roleById(option.roleId).name }}</p>
        </div>
      </div>
    </template>
  </base-filter-select>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import BaseFilterSelect from '@/components/widget/select/BaseFilterSelect.vue'
import { groupItemsByDesk } from '@/model/Utils'

export default {
  name: 'UserSelectBox',
  components: { BaseFilterSelect },
  props: {
    value: {
      required: true,
    },
    nullable: {
      type: Boolean,
      default: false
    },
    multi: {
      type: Boolean,
      default: false
    },
    clearable: {
      type: Boolean,
      default: true
    },
    selectProps: {
      type: Object,
      default: () => ({})
    },
    showRoles: {
      type: Boolean,
      default: false
    },
    roles:{
      type: Array,
      default: () => []
    },
    placeholder: {
      type: String,
      default: ''
    },
    /**
     * If set, the user list is restricted to users whose `businessUnits`
     * set contains this id. Useful when picking a representative for a
     * specific branch (e.g. the duplicate-to-org wizard).
     */
    organizations:{
      type: Array,
      default: () => []
    },
    businessUnits:{
      type: Array,
      default: () => []
    },
    businessUnitId: {
      type: String,
      default: null,
    },

  },
  created() {
    if((this.showRoles || this.roles?.length > 0) && this.allRoles.length === 0) {
      this.refreshData();
    }
  },
  watch: {
    // a role filter switched on after creation (e.g. the task modal's type
    // changed to a transaction task) still needs the role list
    roles(value) {
      if (value?.length > 0 && this.allRoles.length === 0) {
        this.refreshData();
      }
    },
  },
  computed: {
    ...mapGetters('data', ['allUsers', 'allOrganizations']),
    ...mapGetters('users', ['allRoles','roleById']),

    model: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit('input', value);
      }
    },
    // Pass a `selectable` guard so desk header rows can't be picked.
    mergedSelectProps() {
      return { ...this.selectProps, selectable: opt => !(opt && opt._isGroup) };
    },
    userOptions() {
      let users = this.allUsers || [];

      // Test accounts (username / first / last name containing "test", any
      // casing) never show up as selectable representatives/depositors.
      const hasTest = (s) => s != null && String(s).toLowerCase().includes('test');
      users = users.filter(user => !(hasTest(user.username) || hasTest(user.firstName) || hasTest(user.lastName)));

      if(this.roles.length > 0) {
        users = users.filter(user => this.roles.includes((this.roleById(user.roleId) || {}).companyPosition));
      }
      if (this.businessUnitId) {
        // Single explicit business unit always wins (used by picking a rep for
        // one specific branch).
        users = users.filter(user => Array.isArray(user.businessUnits) && user.businessUnits.includes(this.businessUnitId));
      } else {
        // Otherwise apply the organization and business-unit filters together
        // (AND): when the BU filter has values, only users in those branches
        // are shown; the org filter, if any, narrows it further.
        if (this.organizations != null && this.organizations.length > 0) {
          users = users.filter(user => Array.isArray(user.orgIds) && user.orgIds.some(org => this.organizations.includes(org)));
        }
        if (this.businessUnits != null && this.businessUnits.length > 0) {
          users = users.filter(user => Array.isArray(user.businessUnits) && user.businessUnits.some(bu => this.businessUnits.includes(bu)));
        }
      }
      // Group users under the desk of their organization (a user's desk = the
      // desk of the first of their orgs that has one).
      const deskByOrg = {};
      (this.allOrganizations || []).forEach(o => { deskByOrg[o.id] = o.desk; });
      const deskOf = user => {
        const orgIds = Array.isArray(user.orgIds) ? user.orgIds : [];
        for (const oid of orgIds) {
          if (deskByOrg[oid]) return deskByOrg[oid];
        }
        return '';
      };
      const grouped = groupItemsByDesk(users, deskOf, u => u.fullName || u.id);

      if (this.selectProps.nullable) {
        return [
          { id: null, username: 'NOT ASSIGNED', fullName: 'NOT ASSIGNED' },
          ...grouped
        ];
      }

      return grouped;
    }
  },

  methods: {
    ...mapActions('users', ['refreshData']),
  },
};
</script>
